import { v4 as uuidv4 } from 'uuid';

import {
  ATTR,
  Attributes,
  buildLinearAttributes,
  CONTRACT,
  DownloadType,
  EVENT_TYPES,
  EventType,
  isFiniteNumber,
  JobIdState,
  MerchGuideSource,
  OpenCause,
  PogKind,
  sanitizeAttributes,
  toFlag,
  toGuideFileName,
  TrafficAction,
} from './contract';
import { TelemetryContext, viewKey } from './context';
import { TimerSlot } from './scheduler';
import { EventPayload, sendPayload } from './transport';

export const PROBE_INTERVAL_MS = 60 * 1000;
export const ELAPSED_CAP_MS = 75_000;
export const JOB_ID_HOLD_MS = 5_000;

type ContextProvider = () => TelemetryContext;
type Sender = (payload: EventPayload) => void;

/**
 * Hands every event to the transport in a native microtask, i.e. outside any New Relic route-change interaction
 * (the browser agent wraps Promise, not queueMicrotask). Microtasks run FIFO, so issue order = creation order.
 */
const handOffToTransport: Sender = (payload) => {
  try {
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => {
        sendPayload(payload);
      });
    } else {
      sendPayload(payload);
    }
  } catch (e) {
    // Fire-and-forget.
  }
};

export interface TrackerClock {
  /** Monotonic ms; only differences are meaningful. Used for elapsedMs. */
  monoNow: () => number;
  /** Epoch ms. Used for clientTimestamp and the envelope timestamp. */
  wallNow: () => number;
}

const defaultClock: TrackerClock = {
  monoNow: () => (typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()),
  wallNow: () => Date.now(),
};

export interface SeedDownloadInput {
  downloadType: DownloadType;
  success: boolean;
  jobId?: string;
  aisleCode?: string;
  storeNum?: string;
  sourceStoreNum?: string;
  destStoreNum?: string;
  pogKind?: PogKind;
  pogLinearSize?: number | null;
  targetLinearMetric?: number | null;
  productivityEstimate?: number | null;
}

export interface SeedViewInput {
  jobId: string;
  aisleCode?: string;
  storeNum?: string;
  pogKind?: PogKind;
  source?: string;
}

export interface MerchGuideDownloadInput {
  source: MerchGuideSource;
  /** The guide link; sent as `fileName` without query string / fragment. */
  link: string;
  success: boolean;
  storeNum?: string;
  aisleCode?: string;
  aisleCategory?: string;
  pogNumber?: string;
  storeSize?: string;
  orientation?: string;
  merchReleaseDate?: string;
  /** PROTO_BULK only. */
  bulkCount?: number;
}

const isHidden = (): boolean => typeof document !== 'undefined' && document.hidden;

const hasWindowFocus = (): boolean =>
  typeof document === 'undefined' || typeof document.hasFocus !== 'function' || document.hasFocus();

/** The view a jobId hold is waiting on: viewKey minus jobId (a jobId arriving must not itself look like a new view). */
const holdKey = (context: TelemetryContext): string => viewKey({ ...context, jobId: '' });

export class Tracker {
  private readonly uiInstanceId = uuidv4();

  private viewId = uuidv4();

  private context: TelemetryContext | null = null;

  private contextProvider: ContextProvider | null = null;

  private clock: TrackerClock = defaultClock;

  private sender: Sender = handOffToTransport;

  private started = false;

  /** Set by destroy(): the tracker stays off for the rest of the page's life. */
  private destroyed = false;

  /**
   * In the back/forward cache (pagehide with persisted): nothing is sent and no timer is pending until a
   * pageshow with persisted resumes.
   */
  private suspended = false;

  /**
   * Set by a back/forward cache suspension: the newest context seen, which the next start() (the resume, or a
   * start() after stop()) opens through setContext as a new view. The committed `context` is left as it was.
   */
  private reopenContext: TelemetryContext | null = null;

  /** openCause of the first page_open after start(): RESUME after a back/forward cache restore, else START. */
  private firstOpenCause: OpenCause = 'START';

  /** The one pending probe: re-armed after every Traffic event, so it fires PROBE_INTERVAL_MS after the last one. */
  private readonly probeSlot = new TimerSlot();

  private lastFocusState: 'focus' | 'blur' | null = null;

  /** Monotonic time (clock.monoNow) of the previous Traffic event. */
  private lastEventAt: number | null = null;

  /** The tab was hidden at some point since the previous Traffic event sent while visible. */
  private hiddenSinceTraffic = false;

  private contextJobIdState: JobIdState = 'NOT_APPLICABLE';

  /** A context change deferred while the tab was hidden; applied on the next focus (W3). */
  private hiddenPending: { next: TelemetryContext; state: JobIdState } | null = null;

  /** A carriesJobId context still missing its jobId, waiting up to JOB_ID_HOLD_MS for it to arrive (W5). */
  private pending: TelemetryContext | null = null;

  private readonly holdSlot = new TimerSlot();

  /**
   * Whether a `page_open` has actually been sent (as opposed to merely committed via `setContext`
   * before `start()`, which is silent). Drives the `START`/`RESUME` choice when a deferred switch is
   * applied, and whether a same-view `setContext` may drop a still-pending switch.
   */
  private pageOpenSent = false;

  setSender(sender: Sender): void {
    this.sender = sender;
  }

  setClock(clock: TrackerClock): void {
    this.clock = clock;
  }

  setContextProvider(provider: ContextProvider | null): void {
    this.contextProvider = provider;
  }

  /** Re-reads the context from the provider (the Redux store); fires page_open on a view change. */
  refresh(): void {
    if (!this.contextProvider) {
      return;
    }

    try {
      this.setContext(this.contextProvider());
    } catch (e) {
      // A broken selector must never break the app.
    }
  }

  setContext(next: TelemetryContext): void {
    if (this.destroyed) {
      return;
    }

    if (this.suspended || this.reopenContext) {
      this.reopenContext = next;

      return;
    }

    if (this.pending) {
      // holdKey ignores jobId, so a next carrying the awaited view (with or without its jobId yet) is
      // "the same target"; anything else means the user navigated away while still waiting.
      const sameTarget = next.carriesJobId && holdKey(next) === holdKey(this.pending);

      if (!sameTarget) {
        this.cancelHold();
      } else if (next.jobId) {
        this.cancelHold();
        this.switchTo(next, 'RESOLVED');

        return;
      } else {
        // Still waiting; keep the original deadline but carry any newer non-jobId fields.
        this.pending = next;

        return;
      }
    }

    const changed = !this.context || viewKey(this.context) !== viewKey(next);

    if (!changed) {
      this.context = next;

      // Back to the view already committed (this.context): a hidden switch queued in between is moot
      // *if* its page_open was already sent (there is nothing left to reopen). Otherwise (a tab started
      // hidden, or one whose context was set before start(), never actually sent a page_open yet) the
      // queued switch must still fire on the next focus, but it must carry this newest context (e.g. a
      // changed `page`), not the stale one it was holding — a later change can supersede an
      // intermediate, different-view one queued while still hidden (last wins) without this call ever
      // becoming a real view-key change itself.
      if (this.hiddenPending) {
        this.hiddenPending = this.pageOpenSent ? null : { next, state: this.contextJobIdState };
      }

      return;
    }

    if (next.carriesJobId && !next.jobId) {
      // Hold: probes/focus/blur keep reporting the previous (still committed) context and viewId until
      // the jobId arrives or the hold expires.
      this.pending = next;
      this.holdSlot.arm(this.onHoldExpired, JOB_ID_HOLD_MS);

      return;
    }

    this.switchTo(next, next.carriesJobId ? 'RESOLVED' : 'NOT_APPLICABLE');
  }

  private onHoldExpired = (): void => {
    const pending = this.pending;

    this.cancelHold();

    if (pending) {
      this.switchTo(pending, 'UNRESOLVED');
    }
  };

  private cancelHold(): void {
    this.holdSlot.clear();
    this.pending = null;
  }

  /**
   * Applies a view change. While the tab is visible this sends `view_close` (old context, old viewId)
   * followed by `page_open` on the new viewId. While hidden it defers to `hiddenPending`: the change is
   * applied on the next focus instead, with `openCause = 'RESUME'` (or, when no page_open has actually been
   * sent since start(), the first-open cause: `'START'`, or `'RESUME'` after a back/forward cache restore),
   * and no `view_close` is ever sent for it (the preceding `blur` already
   * closed the view). Only called for an actual view-key change; `setContext` clears a stale
   * `hiddenPending` itself when a later change returns to the still-committed `this.context` (and a
   * page_open was already sent for it).
   */
  private switchTo(next: TelemetryContext, state: JobIdState): void {
    if (isHidden()) {
      this.hiddenPending = { next, state };

      return;
    }

    // A switch applied immediately supersedes anything queued while hidden (last wins): the document
    // can flip back to visible before its `visibilitychange` event actually fires.
    this.hiddenPending = null;

    // A committed context with no page_open actually sent yet (a tab that started hidden, made visible
    // before its own visibilitychange event fired) never had a view open to close: no view_close, and
    // the new page_open is the first open (START, or RESUME after a bfcache restore), not a SWITCH — the
    // same rule emitFocusState already applies when the deferred switch is applied on an actual focus.
    const hadOpenView = Boolean(this.context) && this.pageOpenSent;

    if (hadOpenView) {
      this.traffic('view_close');
    }

    this.applySwitch(next, state, hadOpenView ? 'SWITCH' : this.firstOpenCause);
  }

  private applySwitch(next: TelemetryContext, state: JobIdState, cause: OpenCause): void {
    this.viewId = uuidv4();
    this.context = next;
    this.contextJobIdState = state;
    this.traffic('page_open', { [ATTR.jobIdState]: state, [ATTR.openCause]: cause });
  }

  getContext(): TelemetryContext | null {
    return this.context;
  }

  getViewId(): string {
    return this.viewId;
  }

  getUiInstanceId(): string {
    return this.uiInstanceId;
  }

  start(): void {
    if (this.started || this.destroyed || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    this.suspended = false;
    this.lastFocusState = !isHidden() && hasWindowFocus() ? 'focus' : 'blur';
    this.hiddenSinceTraffic = this.hiddenSinceTraffic || isHidden();
    this.armProbe();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('blur', this.onWindowBlur);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);

    const reopen = this.reopenContext;

    if (reopen) {
      // The newest context goes through setContext again (re-read from the store when there is one): a new
      // viewId, its own jobIdState and the jobId hold rule.
      this.reopenContext = null;
      this.context = null;

      if (this.contextProvider) {
        this.refresh();
      } else {
        this.setContext(reopen);
      }
    } else if (this.context) {
      if (isHidden()) {
        // A context can already be committed here (e.g. set by the router middleware's refresh() before
        // startTelemetry() runs, or by a direct setContext() call) even though no page_open has ever
        // actually been sent for it. Keep it as the committed context (getContext() must keep returning
        // it, and any SeedView/SeedDownload/MerchGuideDownload fired from this still-hidden tab must carry
        // it) and simply queue it as the pending switch; pageOpenSent (still false here) is what makes the
        // deferred apply on the next focus use openCause 'START' rather than 'RESUME'.
        this.hiddenPending = { next: this.context, state: this.contextJobIdState };
      } else {
        this.traffic('page_open', { [ATTR.jobIdState]: this.contextJobIdState, [ATTR.openCause]: 'START' });
      }
    } else {
      this.refresh();
    }
  }

  /** Stops until the next start(). */
  stop(): void {
    if (this.started || this.suspended) {
      this.teardown();
    }
  }

  /** Stops for the rest of the page's life (pagehide, logout): later start/setContext/track calls do nothing. */
  destroy(): void {
    this.destroyed = true;
    this.teardown();
  }

  /** Entering the back/forward cache: off until a pageshow with persisted calls resume(). */
  private suspend(): void {
    // A pending hold is cancelled and a switch deferred while hidden is dropped; the newest of them is reopened.
    const latest = this.pending ?? this.hiddenPending?.next ?? this.context;

    this.teardown(true);
    this.reopenContext = latest;
  }

  /** Back from the back/forward cache: restarts and opens the current view as a RESUME on a fresh viewId. */
  private resume(): void {
    this.firstOpenCause = 'RESUME';
    this.start();
  }

  private teardown(suspending = false): void {
    this.started = false;
    this.suspended = suspending;
    this.probeSlot.clear();

    if (typeof window !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('focus', this.onWindowFocus);
      window.removeEventListener('blur', this.onWindowBlur);
      window.removeEventListener('pagehide', this.onPageHide);

      if (!suspending) {
        window.removeEventListener('pageshow', this.onPageShow);
      }
    }

    this.hiddenPending = null;
    this.cancelHold();
    // A later start() must not treat a still-hidden tab's first open as a RESUME just because a
    // page_open was sent before it was stopped.
    this.pageOpenSent = false;
    this.firstOpenCause = 'START';
  }

  isStarted(): boolean {
    return this.started;
  }

  trackSeedDownload(input: SeedDownloadInput): void {
    const {
      downloadType,
      success,
      storeNum,
      pogLinearSize,
      targetLinearMetric,
      productivityEstimate,
      ...rest
    } = input;

    this.send(EVENT_TYPES.SeedDownload, {
      ...rest,
      [ATTR.downloadType]: downloadType,
      ...buildLinearAttributes({ pogLinearSize, targetLinearMetric }),
      ...(isFiniteNumber(productivityEstimate) ? { [ATTR.productivityEstimate]: productivityEstimate } : {}),
      [ATTR.success]: toFlag(success),
    }, storeNum);
  }

  trackSeedView({ source = 'SEED_SEARCH', storeNum, ...rest }: SeedViewInput): void {
    this.send(EVENT_TYPES.SeedView, { ...rest, [ATTR.source]: source }, storeNum);
  }

  /** An Execution Guide belongs to a store + aisle, never to a seed: jobId is always "" (contract §4.4). */
  trackMerchGuideDownload({ link, success, storeNum, source, bulkCount, ...rest }: MerchGuideDownloadInput): void {
    this.send(EVENT_TYPES.MerchGuideDownload, {
      ...rest,
      [ATTR.jobId]: '',
      [ATTR.source]: source,
      [ATTR.fileName]: toGuideFileName(link),
      ...(source === 'PROTO_BULK' && isFiniteNumber(bulkCount) ? { [ATTR.bulkCount]: bulkCount } : {}),
      [ATTR.success]: toFlag(success),
    }, storeNum);
  }

  private traffic(action: TrafficAction, extra: Record<string, unknown> = {}): void {
    // A forgotten background tab must not count as active time (contract §4.1); blur is the exception,
    // it is what closes the visible interval.
    if (!this.started || (action !== 'blur' && isHidden())) {
      return;
    }

    // No view has actually opened yet (first load, still inside the initial jobId hold): probes, focus
    // and blur have nothing to report against. Only page_open (the hold resolving/expiring, or a normal
    // first open) may pass through with a null context.
    if (this.context === null && action !== 'page_open') {
      return;
    }

    if (action === 'page_open') {
      // Only set once the event has actually passed the guard above (i.e. is really going to be sent).
      this.pageOpenSent = true;
    }

    const now = this.clock.monoNow();
    const last = this.lastEventAt;
    const visible = !isHidden();
    // page_open starts a fresh interval. Every other action reports the time since the previous Traffic
    // event (lastEventAt is updated by Traffic only, so a SeedDownload in between never resets it), capped
    // so a laptop asleep for hours does not inflate a session's active time. A focus after the tab was
    // hidden reports 0: hidden time never counts.
    const elapsedMs = last === null || action === 'page_open' || (action === 'focus' && this.hiddenSinceTraffic)
      ? 0
      : Math.min(ELAPSED_CAP_MS, Math.max(0, Math.round(now - last)));

    this.lastEventAt = now;
    this.armProbe();

    // The blur sent on visibilitychange->hidden is itself sent while hidden and must not clear the flag.
    if (visible) {
      this.hiddenSinceTraffic = false;
    }

    this.send(EVENT_TYPES.Traffic, {
      ...extra,
      [ATTR.action]: action,
      [ATTR.visible]: toFlag(visible),
      [ATTR.focused]: toFlag(visible && this.lastFocusState === 'focus'),
      [ATTR.success]: 'true',
      [ATTR.elapsedMs]: elapsedMs,
    });
  }

  private send(eventType: EventType, extra: Record<string, unknown>, storeNumOverride?: string): void {
    if (this.destroyed || this.suspended) {
      return;
    }

    try {
      if (!this.context && !this.pending) {
        this.refresh();
      }

      // A SeedView/SeedDownload/MerchGuideDownload fired during the initial jobId hold still needs a
      // route (component/page/aisleCode); the pending (not-yet-committed) context carries it.
      const context = this.context ?? this.pending;
      const now = this.clock.wallNow();

      // seq is stamped by the transport when the request is actually issued.
      const common: Attributes = {
        [ATTR.component]: context?.component || 'OTHER',
        [ATTR.page]: context?.page || '',
        [ATTR.mode]: context?.mode || 'VIEW',
        [ATTR.viewId]: this.viewId,
        [ATTR.uiInstanceId]: this.uiInstanceId,
        [ATTR.jobId]: context?.jobId || '',
        [ATTR.aisleCode]: context?.aisleCode || '',
        [ATTR.clientTimestamp]: now,
        [ATTR.contract]: CONTRACT.name,
        [ATTR.contractVersion]: CONTRACT.version,
      };

      this.sender({
        event_type: eventType,
        store_num: storeNumOverride ?? context?.storeNum ?? '',
        timestamp: now,
        // eventId (the idempotency key) goes after the extras so they can never override it.
        attributes: { ...common, ...sanitizeAttributes(extra), [ATTR.eventId]: uuidv4() },
      });
    } catch (e) {
      // Fire-and-forget.
    }
  }

  /** Arms the one probe PROBE_INTERVAL_MS after the last Traffic event (after now, when there is none yet). */
  private armProbe(): void {
    const now = this.clock.monoNow();
    const from = this.lastEventAt ?? now;

    this.probeSlot.arm(this.onProbeTick, Math.max(0, from + PROBE_INTERVAL_MS - now));
  }

  private onProbeTick = (): void => {
    this.traffic('probe');

    // Not sent (hidden, or no view open yet): keep the one probe pending, a full interval from now (no catch-up).
    if (this.started && !this.probeSlot.isArmed()) {
      this.probeSlot.arm(this.onProbeTick, PROBE_INTERVAL_MS);
    }
  };

  /**
   * Closes the visible interval with a blur (unless visibilitychange already sent it), then shuts down: for good
   * on a real unload, until the matching pageshow when the page enters the back/forward cache (persisted).
   */
  private onPageHide = (event: PageTransitionEvent): void => {
    this.emitFocusState('blur');

    if (event.persisted) {
      this.suspend();
    } else {
      this.destroy();
    }
  };

  private onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted && this.suspended) {
      this.resume();
    }
  };

  private onVisibilityChange = (): void => {
    if (isHidden()) {
      this.hiddenSinceTraffic = true;
    }

    this.emitFocusState(isHidden() ? 'blur' : 'focus');
  };

  private onWindowFocus = (): void => {
    this.emitFocusState('focus');
  };

  private onWindowBlur = (): void => {
    this.emitFocusState('blur');
  };

  private emitFocusState(state: 'focus' | 'blur'): void {
    // A focus event can arrive while the document is still hidden; wait for it to become visible.
    if (this.lastFocusState === state || (state === 'focus' && isHidden())) {
      return;
    }

    this.lastFocusState = state;

    if (state === 'focus' && this.hiddenPending) {
      const pending = this.hiddenPending;

      this.hiddenPending = null;
      // No page_open has actually been sent yet since start() -> the first open (START, or RESUME after a
      // bfcache restore); otherwise this switch was deferred while hidden and the old view was already
      // closed by blur -> RESUME.
      this.applySwitch(pending.next, pending.state, this.pageOpenSent ? 'RESUME' : this.firstOpenCause);
    }

    this.traffic(state);
  }
}

export const tracker = new Tracker();
